import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { MdChevronRight } from "react-icons/md";
import { Link as RouterLink } from "react-router-dom";

import EmptyState from "@/components/Core/EmptyState";
import { routers } from "@/configs";
import { useTranslate } from "@/hooks";
import { SportIconTile } from "@/pages/Sport/components/styles";
import { getSportIcon } from "@/pages/Sport/data/sportIcons";
import { useEventsByStatus } from "@/pages/Sport/hooks/useEventsByStatus";
import { searchSportEvents } from "@/services";
import { colors } from "@/theme";
import { EventsByStatusResponse, SportSearchEvent } from "@/types";

import { MIN_LENGTH, SectionTitle } from "./styles";

const LIVE_LIMIT = 8;
const SOON_LIMIT = 12;

const eventPath = (event: SportSearchEvent) =>
  routers.SportEvent.replace(":sportSlug", event.sport.slug)
    .replace(":regionSlug", event.region?.slug || "all")
    .replace(":leagueSlug", event.league?.slug || "all")
    .replace(":eventId", String(event.eventId))
    .replace(":eventStatus", event.listStatus);

/** The by-status lists in the shape the search returns, so one row draws both. */
const flatten = (
  data: EventsByStatusResponse | undefined,
  listStatus: SportSearchEvent["listStatus"],
): SportSearchEvent[] =>
  (data?.sports ?? []).flatMap((group) =>
    group.leagues.flatMap((league) =>
      league.events.map((event) => ({
        eventId: event.eventId,
        home: event.home,
        away: event.away,
        date: event.date,
        status: event.status,
        listStatus,
        sport: event.sport ?? group.sport,
        league: event.league ?? league.league,
        region: event.region ?? league.region,
        scores: event.scores,
      })),
    ),
  );

const kickOff = (iso: string) => {
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return {
    day: sameDay ? null : date.toLocaleDateString([], { day: "2-digit", month: "short" }),
    time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
};

const EventRow = ({ event }: { event: SportSearchEvent }) => {
  const t = useTranslate();
  const Icon = getSportIcon(event.sport.slug);
  const live = event.listStatus === "live" || event.status === "live";
  const start = kickOff(event.date);

  return (
    <ButtonBase
      component={RouterLink}
      to={eventPath(event)}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        p: 1.5,
        textAlign: "left",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(colors.prime, 0.14),
        backgroundColor: alpha(colors.prime, 0.04),
        transition: "border-color 150ms ease, background-color 150ms ease",
        "&:hover": {
          borderColor: alpha(colors.prime, 0.5),
          backgroundColor: alpha(colors.prime, 0.08),
        },
        "&:focus-visible": { outline: `2px solid ${colors.prime}`, outlineOffset: 2 },
        ...theme.applyStyles("dark", { backgroundColor: alpha(colors.prime, 0.07) }),
      })}
    >
      <SportIconTile aria-hidden>
        <Icon size={15} />
      </SportIconTile>

      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {event.home}
        </Typography>
        <Typography variant="body2" fontWeight={700} noWrap>
          {event.away}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {event.sport.name} · {event.league.name}
        </Typography>
      </Stack>

      {/* Live: the score and the tag; to come: when it starts. */}
      <Stack alignItems="flex-end" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
        {live ? (
          <>
            <Typography
              component="span"
              sx={{
                px: 0.75,
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: colors.white,
                backgroundColor: "error.main",
              }}
            >
              {t("sport.live")}
            </Typography>
            {event.scores && (
              <Typography variant="body2" fontWeight={800} sx={{ mt: 0.5 }}>
                {event.scores.home} – {event.scores.away}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" fontWeight={700}>
              {start.time}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {start.day ?? t("sport.today")}
            </Typography>
          </>
        )}
      </Stack>
      <Box component={MdChevronRight} aria-hidden sx={{ flexShrink: 0, color: "text.secondary" }} />
    </ButtonBase>
  );
};

const EventGrid = ({ events }: { events: SportSearchEvent[] }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1,
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(3, 1fr)" },
    }}
  >
    {events.map((event) => (
      <EventRow key={event.eventId} event={event} />
    ))}
  </Box>
);

const RowsSkeleton = ({ count = 6 }: { count?: number }) => (
  <Box
    sx={{
      display: "grid",
      gap: 1,
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(3, 1fr)" },
    }}
  >
    {Array.from({ length: count }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={78} sx={{ borderRadius: 2.5 }} />
    ))}
  </Box>
);

/**
 * Matches: until the term is long enough to search, what is live and what starts in the
 * next day; then every live or upcoming match whose team or league has it.
 */
const SportResults = ({ query }: { query: string }) => {
  const t = useTranslate();
  const ready = query.length >= MIN_LENGTH;

  const live = useEventsByStatus("live", { fetch: !ready });
  const soon = useEventsByStatus("next24", { fetch: !ready });
  const search = useQuery({
    queryKey: ["sport-search", query.toLowerCase()],
    queryFn: () => searchSportEvents(query),
    enabled: ready,
    staleTime: 15_000,
  });

  const liveEvents = useMemo(() => flatten(live.data, "live").slice(0, LIVE_LIMIT), [live.data]);
  const soonEvents = useMemo(
    () =>
      flatten(soon.data, "next24")
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(0, SOON_LIMIT),
    [soon.data],
  );

  if (ready) {
    if (search.isPending) return <RowsSkeleton />;
    if (search.isError) return <EmptyState title="sport.search-error" />;
    if (search.data.length === 0) return <EmptyState title="sport.search-empty" />;
    return <EventGrid events={search.data} />;
  }

  const loading = live.isPending || soon.isPending;
  if (!loading && liveEvents.length === 0 && soonEvents.length === 0) {
    return <EmptyState title="search.no-events" />;
  }

  return (
    <Stack gap={3}>
      {(live.isPending || liveEvents.length > 0) && (
        <Stack gap={1.5}>
          <SectionTitle>{t("search.live-now")}</SectionTitle>
          {live.isPending ? <RowsSkeleton count={2} /> : <EventGrid events={liveEvents} />}
        </Stack>
      )}
      {(soon.isPending || soonEvents.length > 0) && (
        <Stack gap={1.5}>
          <SectionTitle>{t("search.starting-soon")}</SectionTitle>
          {soon.isPending ? <RowsSkeleton /> : <EventGrid events={soonEvents} />}
        </Stack>
      )}
    </Stack>
  );
};

export default SportResults;
